import CheckoutConfig from '@/../PaymentIQCheckoutClientConfig'
import Store from '@/store'
import BankIdLogo from '@/assets/icons/bankId.svg'
import BankIdNoLogo from '@/assets/icons/bankIdNo.svg'
import nemIdLogo from '@/assets/icons/nemId.svg'
import frejaIdLogo from '@/assets/icons/frejaId.png'
import manualLogo from '@/assets/icons/manual.svg'
import GetIdentifyData from '@/utils/identifyFields/countries'
import { CountryItem } from '@/utils/identifyFields/countries/countryTypes'
import { ProviderType } from '@/utils/identifyFields/providers/providerTypes'

export function inIframe () {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

// We accept a font string to be passed in as a comma-separated configuration.
// {source}, {fontFamily}, {optional: urlToSource}, for example:
// google, Roboto
// custom, Santander, santander --> When a custom handler is used, we will point to a static css-file located on https://static.paymentiq.io/assets/fonts/custom-font-{urlToSource}.css
// In that file, the unique endpoints are defined to the font.
export const generateFontConfig = (urlParams) => {
  try {
    const { font } = getParams(urlParams)
    let config: any = null
    if (font) {
      const fontSources = ['google', 'custom']
      const source = font.split(',')[0].trim()
      const fontFamily = font.split(',')[1].trim()
      if (fontSources.includes(source) && fontFamily) {
        config = {
          [source]: {
            families: [fontFamily]
          }
        }
        if (source === 'custom') {
          // Unique part for every customer - points to a css-file on static that references to their custom fonts.
          const customUrlSuffix = font.split(',')[2].trim()
          config.custom.urls = [`https://static.paymentiq.io/assets/fonts/custom-font-${customUrlSuffix}.css?env=paymentiq-checkout-client`] // add ?env to trigger a cache reset, otherwise blocked by cors
          switch (customUrlSuffix) {
            case 'santander':
              document.getElementsByTagName('html')[0].style.setProperty(`--font-adjustment`, '-2px')
              break
          }
        }
        document.getElementsByTagName('html')[0].style.setProperty(`--font-family`, fontFamily)
      }
    }
    return config
  } catch (err) {
    console.error('Unable to configure custom font, passed in configuration:', getParams(urlParams).font)
    console.error(err)
    return null
  }
}

// returns object of all the passed in setup/config properties
export const getParams = (url): any => {
  var params = {}
  var parser = document.createElement('a')
  parser.href = url
  var query = parser.search.substring(1)
  var vars = query.split('&')
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=')
    params[pair[0]] = decodeURIComponent(pair[1])
  }
  return params
}

export const deviceIdExists = () => {
  const deviceId = localStorage.getItem('piq_dId')
  return deviceId && deviceId !== 'undefined'
}

export const setLocalStorageAndCookie = (key, value) => {
  let stringified = typeof value !== 'string' ? JSON.stringify(value) : value
  localStorage.setItem(key, stringified)
}

export const existsInObject = (key, state): boolean => {
  return state.hasOwnProperty(key)
}

// transform an object into a queryString foo=bar&x=y
export const transformObjectToQuery = (object): string => {
  if (object.nationalId) {
    object.ssn = object.nationalId
    delete object.nationalId
  }
  if (object.zip) {
    // zip code doesn't match in did if it contains spaces
    let postalCode = object.zip
    postalCode = postalCode.split(' ').join('')
    object.postal_code = postalCode
    delete object.zip
  }
  try {
    return Object.keys(object).map(key => key + '=' + object[key]).join('&')
  } catch (err) {
    throw err
  }
}

export function maskValue (value: string, length: number = 6, offset: number = 0) {
  let maskValue = ''
  for (let i = 0; i < length; i++) {
    maskValue += '•'
  }
  if (offset === 0) {
    return `${value.slice(offset, -length)} ${maskValue}`
  } else {
    let start = value.substring(0, offset)
    let end = value.substring(offset + length, value.length)
    return `${start} ${maskValue} ${end}`
  }
}

export function getOuterHeight (): number {
  const outerEl = document.getElementById('app')
  if (outerEl && outerEl.offsetHeight > 0) {
    return outerEl.offsetHeight + 25
  } else {
    return 450 // default height
  }
}

export function hasProperty (object: object, key: string) {
  return object ? Object.prototype.hasOwnProperty.call(object, key) : false
}

// Returns a function, that, as long as it continues to be invoked, will not
// be triggered. The function will be called after it stops being called for
// N milliseconds. If `immediate` is passed, trigger the function on the
// leading edge, instead of the trailing.
export function debounce (func: any, wait: number, immediate?: boolean) {
  let timeout
  return function () {
    const context = this
    const args = arguments
    var later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    var callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
};

// returns the selected country settings or the selected identifyProvider (bankId, nemId, frejaId etc)
export function getIdentifyProvider (country: string, identifyProvider: string): ProviderType {
  const countryData: CountryItem = GetIdentifyData[country]()
  return countryData.providers.find((provider: ProviderType): boolean => (provider.id === identifyProvider))
}

export function isUserMobile (): boolean {
  var check = false;
  (function (a) { if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substr(0, 4))) check = true })(navigator.userAgent || navigator.vendor)
  return check
}

export function providerLogo (provider) {
  const logos = {
    bankId: BankIdLogo,
    bankIdNo: BankIdNoLogo,
    nemId: nemIdLogo,
    frejaId: frejaIdLogo,
    manual: manualLogo
  }
  if (provider) {
    return logos[provider]
  }
}

export function missingMandatoryData (mappedData): boolean {
  const email = mappedData.email && mappedData.email !== ''
  // add more properties we want to check here!
  return !email
}

export const scrollToNotice = () => {
  // You need to set the Notice ID as:  <Notice id="notice-sign"/>
  const notice = document.querySelector('#notice-sign')
  notice && notice.scrollIntoView && notice.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
}

export const scrollTo = (scrollPosition) => {
  if (scrollPosition || scrollPosition === 0) {
    window.scroll(0, scrollPosition)
  }
}
